// src/admin/screens/loans/tabs/OverviewTab.tsx
// Two-column overview matching the Thandizo Admin Console design's hero loan
// layout. Left column: decision summary (status + headline metrics + rationale)
// → cost breakdown → application timeline. Right column: borrower mini-card,
// schedule progress, purpose.
//
// Real data only — fields come straight from useLoan's LoanDetail. The credit
// engine card is intentionally light on placeholder figures (PD, utilisation
// from a separate scoring service) until that integration ships; the rationale
// text calls that out so the screen doesn't pretend to know more than it does.
import {
  Sparkles, FilePlus, CheckCircle2, Calculator, Clock, Zap, CalendarDays, ExternalLink, AlertCircle,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Card from '@admin/components/Card';
import Money from '@admin/components/Money';
import Avatar from '@admin/components/Avatar';
import Chip from '@admin/components/Chip';
import Button from '@admin/components/Button';
import { formatCatDate, formatCatDateTime } from '@admin/utils/dates';
import { formatKwacha } from '@admin/utils/money';
import type { Ngwee } from '@/lib/types';
import type { LoanDetail } from '@admin/hooks/useLoan';
import type { Database } from '@/lib/database.types';
import styles from './OverviewTab.module.css';

type LoanStatus = Database['public']['Enums']['loan_status'];

interface Props {
  loan: LoanDetail;
}

export default function OverviewTab({ loan }: Props) {
  const recommendation = recommendationFor(loan.status);
  const earPercent = (loan.effective_annual_rate_bps / 100).toFixed(1);
  const totalRepayable = loan.total_repayable_ngwee;
  const installments = loan.loan_repayment_schedule.length;
  const installmentsPaid = loan.loan_repayment_schedule.filter((s) => s.status === 'paid').length;

  const borrowerName = loan.profiles?.display_name ?? loan.profiles?.full_name ?? '—';
  const borrowerPhone = loan.profiles?.phone ?? '';
  const borrowerId = loan.profiles?.id;

  const timeline = buildTimeline(loan);

  return (
    <div className={styles.grid}>
      <div className={styles.col}>
        <Card accent>
          <div className={styles.eyebrow}>
            <Sparkles size={14} aria-hidden="true" />
            Decision summary
          </div>
          <div className={styles.decisionGrid}>
            <div>
              <div className={styles.statLabel}>Status</div>
              <Chip tone={recommendation.tone} icon={<recommendation.Icon size={12} />}>
                {recommendation.label}
              </Chip>
            </div>
            <div>
              <div className={styles.statLabel}>EAR</div>
              <div className={`${styles.statValue} ${earTone(loan.effective_annual_rate_bps)}`}>{earPercent}%</div>
            </div>
            <div>
              <div className={styles.statLabel}>Term</div>
              <div className={styles.statValue}>{loan.term_days}d</div>
            </div>
            <div>
              <div className={styles.statLabel}>Principal</div>
              <div className={styles.statValue}>
                {formatKwacha(loan.principal_ngwee as Ngwee, { decimals: 0 })}
              </div>
            </div>
          </div>
          <div className={styles.note}>
            <strong>Rationale:</strong> {rationaleFor(loan)}
          </div>
        </Card>

        <Card>
          <h3 className={styles.cardTitle}>Cost breakdown</h3>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Principal</span>
            <span className={styles.rowVal}><Money amount={loan.principal_ngwee as Ngwee} /></span>
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Interest ({earPercent}% EAR)</span>
            <span className={styles.rowVal}><Money amount={loan.interest_ngwee as Ngwee} /></span>
          </div>
          <div className={styles.row}>
            <span className={styles.rowLabel}>Processing fee</span>
            <span className={styles.rowVal}><Money amount={loan.fee_ngwee as Ngwee} /></span>
          </div>
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total repayable</span>
            <span className={styles.totalVal}>
              {totalRepayable != null
                ? formatKwacha(totalRepayable as Ngwee, { decimals: 0 })
                : '—'}
            </span>
          </div>
          {loan.due_date && (
            <div className={styles.note} style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <CalendarDays size={14} style={{ color: 'var(--c-p700)', flexShrink: 0, marginTop: 2 }} />
              <div>
                {installments > 0
                  ? <>{installments} installment{installments === 1 ? '' : 's'} · </>
                  : null}
                First due <strong>{formatCatDate(loan.due_date)}</strong>
              </div>
            </div>
          )}
        </Card>

        <Card>
          <h3 className={styles.cardTitle}>Application timeline</h3>
          <div className={styles.timeline}>
            {timeline.map((step, i) => (
              <div key={i} className={styles.tlRow}>
                {i < timeline.length - 1 && <span className={styles.tlConnector} aria-hidden="true" />}
                <span className={`${styles.tlIcon} ${styles[step.state]}`}>
                  <step.Icon size={14} />
                </span>
                <div className={styles.tlBody}>
                  <div className={styles.tlHead}>
                    <span className={styles.tlTitle}>{step.title}</span>
                    {step.time && <span className={styles.tlTime}>{step.time}</span>}
                  </div>
                  {step.desc && <div className={styles.tlDesc}>{step.desc}</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className={styles.col}>
        <Card>
          <div className={styles.borrowerHead}>
            <Avatar name={borrowerName} size={48} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className={styles.borrowerName}>{borrowerName}</div>
              {borrowerPhone && <div className={styles.borrowerPhone}>{borrowerPhone}</div>}
            </div>
            {borrowerId && (
              <Link to={`/borrowers/${borrowerId}`} style={{ textDecoration: 'none' }}>
                <Button variant="ghost" size="sm" rightIcon={<ExternalLink size={13} />}>Profile</Button>
              </Link>
            )}
          </div>
          <div className={styles.borrowerStats}>
            <StatTile label="Loan number" value={loan.loan_number} />
            <StatTile label="Term" value={`${loan.term_days} days`} />
            <StatTile label="Installments" value={`${installmentsPaid} / ${installments || '—'}`} />
            <StatTile label="Payments recorded" value={String(loan.payments.length)} />
            <StatTile label="Applied" value={formatCatDate(loan.applied_at)} />
            <StatTile label="Due" value={loan.due_date ? formatCatDate(loan.due_date) : '—'} />
          </div>
        </Card>

        <Card title="Purpose">
          {loan.purpose
            ? <p className={styles.purpose}>{loan.purpose}</p>
            : <p className={styles.purposeEmpty}>Not provided.</p>}
        </Card>

        {loan.cancellation_reason && (
          <Card>
            <div className={styles.eyebrow} style={{ color: 'var(--c-danger)' }}>
              <AlertCircle size={14} aria-hidden="true" />
              Cancellation reason
            </div>
            <p className={styles.purpose}>{loan.cancellation_reason}</p>
          </Card>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.statTile}>
      <div className={styles.statTileLabel}>{label}</div>
      <div className={styles.statTileValue}>{value}</div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────
function earTone(bps: number): string {
  if (bps >= 5000) return styles.danger ?? '';
  if (bps >= 3000) return styles.warn ?? '';
  return styles.success ?? '';
}

interface Recommendation {
  label: string;
  tone: 'success' | 'warning' | 'danger' | 'brand' | 'neutral' | 'info';
  Icon: LucideIcon;
}

function recommendationFor(status: LoanStatus): Recommendation {
  switch (status) {
    case 'pending_approval': return { label: 'Awaiting decision', tone: 'warning', Icon: Clock };
    case 'approved': return { label: 'Approved', tone: 'success', Icon: CheckCircle2 };
    case 'disbursement_pending': return { label: 'Disbursing', tone: 'info', Icon: Zap };
    case 'active': return { label: 'Active', tone: 'success', Icon: CheckCircle2 };
    case 'repaid': return { label: 'Repaid', tone: 'success', Icon: CheckCircle2 };
    case 'delinquent': return { label: 'Delinquent', tone: 'warning', Icon: AlertCircle };
    case 'default': return { label: 'Default', tone: 'danger', Icon: AlertCircle };
    case 'restructured': return { label: 'Restructured', tone: 'neutral', Icon: AlertCircle };
    case 'cancelled': return { label: 'Cancelled', tone: 'neutral', Icon: AlertCircle };
    case 'disbursement_failed': return { label: 'Disbursement failed', tone: 'danger', Icon: AlertCircle };
  }
}

function rationaleFor(loan: LoanDetail): string {
  switch (loan.status) {
    case 'pending_approval':
      return 'Application is queued for review. Approve to disburse to the borrower’s registered mobile money account; reject to notify the borrower via SMS.';
    case 'approved':
      return 'Approved by an admin. Awaiting disbursement to the borrower’s mobile money account.';
    case 'active':
      return 'Loan is in repayment. Track installment progress and recorded payments below.';
    case 'delinquent':
      return 'Loan is past due. Coordinate with collections and consider a restructure if appropriate.';
    case 'repaid':
      return 'All installments are settled. No further action required.';
    case 'default':
      return 'Loan was written off. The case remains visible for audit and recovery follow-up.';
    case 'cancelled':
      return loan.cancellation_reason ?? 'Application was cancelled before disbursement.';
    case 'disbursement_failed':
      return 'Mobile money disbursement failed. Retry once the borrower’s wallet is verified, or cancel the loan.';
    case 'disbursement_pending':
      return 'Disbursement request sent to the aggregator; awaiting confirmation.';
    case 'restructured':
      return 'Loan terms were modified. Treat as active for collections purposes.';
  }
}

interface Step {
  title: string;
  desc: string;
  time: string | null;
  state: 'done' | 'current' | 'todo' | 'failed';
  Icon: LucideIcon;
}

function buildTimeline(loan: LoanDetail): Step[] {
  const steps: Step[] = [];

  steps.push({
    title: 'Application submitted',
    desc: 'Borrower submitted via mobile app',
    time: formatCatDateTime(loan.applied_at) || null,
    state: 'done',
    Icon: FilePlus,
  });

  steps.push({
    title: 'Automated checks passed',
    desc: 'KYC verified · phone verified · active-loan rule passed',
    time: null,
    state: 'done',
    Icon: CheckCircle2,
  });

  steps.push({
    title: 'Cost computed',
    desc: `Principal + interest + fee = total repayable`,
    time: null,
    state: 'done',
    Icon: Calculator,
  });

  if (loan.approved_at) {
    steps.push({
      title: 'Approved',
      desc: 'Admin approved the application',
      time: formatCatDateTime(loan.approved_at),
      state: 'done',
      Icon: CheckCircle2,
    });
  } else if (loan.status === 'pending_approval') {
    steps.push({
      title: 'Awaiting decision',
      desc: 'Pending admin approval',
      time: null,
      state: 'current',
      Icon: Clock,
    });
  } else if (loan.status === 'cancelled') {
    steps.push({
      title: 'Cancelled',
      desc: loan.cancellation_reason ?? 'Application was cancelled',
      time: null,
      state: 'failed',
      Icon: AlertCircle,
    });
  }

  if (loan.disbursed_at) {
    steps.push({
      title: 'Disbursed',
      desc: 'Funds sent to borrower’s mobile money account',
      time: formatCatDateTime(loan.disbursed_at),
      state: 'done',
      Icon: Zap,
    });
  } else if (loan.status === 'disbursement_pending' || loan.status === 'approved') {
    steps.push({
      title: 'Disbursing',
      desc: 'Awaiting confirmation from aggregator',
      time: null,
      state: 'current',
      Icon: Zap,
    });
  } else if (loan.status === 'disbursement_failed') {
    steps.push({
      title: 'Disbursement failed',
      desc: 'Aggregator reported a failure; retry or cancel',
      time: null,
      state: 'failed',
      Icon: AlertCircle,
    });
  }

  if (loan.fully_repaid_at) {
    steps.push({
      title: 'Fully repaid',
      desc: 'All installments settled',
      time: formatCatDateTime(loan.fully_repaid_at),
      state: 'done',
      Icon: CheckCircle2,
    });
  } else if (loan.due_date && loan.status === 'active') {
    steps.push({
      title: 'Repayment in progress',
      desc: `Final installment due ${formatCatDate(loan.due_date)}`,
      time: null,
      state: 'current',
      Icon: CalendarDays,
    });
  }

  return steps;
}
